/**
 * Mapbox Places Service
 * 
 * Fallback for POI suggestions when OpenAI fails
 * Uses Mapbox Geocoding API + Search API
 */

import { calculateDistance } from '@my-roadtrip/utils'
import { createLogger } from '../common/utils/logger.js'

const logger = createLogger('MapboxPlacesService')
const MAPBOX_ACCESS_TOKEN = process.env.MAPBOX_ACCESS_TOKEN || ''

export interface PlaceResult {
  name: string
  description: string
  location: { coordinates: { lat: number; lng: number } }
  category: string
  rating?: number
}

export interface PlaceVerificationResult {
  matched: boolean
  confidence: number
  distanceMeters?: number
  name?: string
  location?: { coordinates: { lat: number; lng: number } }
  source: 'mapbox'
}

export class MapboxPlacesService {
  private readonly baseUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places'

  /**
   * Search nearby places using Mapbox Geocoding API
   */
  async searchNearby(
    location: { lat: number; lng: number },
    type: string,
    radiusMeters: number = 5000
  ): Promise<PlaceResult[]> {
    if (!MAPBOX_ACCESS_TOKEN) {
      logger.warn('MAPBOX_ACCESS_TOKEN not set, returning empty results')
      return []
    }

    try {
      // Map category to Mapbox POI types
      const mapboxType = this.mapCategoryToMapboxType(type)
      
      // Mapbox search query (proximity search)
      const url = new URL(`${this.baseUrl}/${mapboxType}.json`)
      url.searchParams.set('proximity', `${location.lng},${location.lat}`)
      url.searchParams.set('limit', '10')
      url.searchParams.set('access_token', MAPBOX_ACCESS_TOKEN)
      url.searchParams.set('types', 'poi')

      const response = await fetch(url.toString())
      
      if (!response.ok) {
        throw new Error(`Mapbox API error: ${response.statusText}`)
      }

      const data = await response.json() as {
        features: Array<{
          text?: string
          place_name?: string
          properties?: { address?: string }
          geometry: {
            coordinates: [number, number]
          }
        }>
      }

      const places = data.features.map((feature) => {
        const lat = feature.geometry.coordinates[1]
        const lng = feature.geometry.coordinates[0]
        const distanceMeters = calculateDistance(location, { lat, lng })

        return {
          place: {
            name: feature.text || feature.place_name || 'Unknown',
            description: feature.properties?.address || feature.place_name || '',
            location: { coordinates: { lat, lng } },
            category: type,
            rating: undefined as number | undefined
          },
          distanceMeters
        }
      })
      .filter(({ distanceMeters }) => distanceMeters <= radiusMeters)
      .slice(0, 5)
      .map(({ place }) => place)

      return places
    } catch (error) {
      logger.error('Mapbox Places API error', { error })
      return []
    }
  }

  /**
   * Map Google Places categories to Mapbox POI types
   */
  private mapCategoryToMapboxType(category: string): string {
    const categoryMap: Record<string, string> = {
      restaurant: 'restaurant',
      cafe: 'cafe',
      bar: 'bar',
      museum: 'museum',
      park: 'park',
      tourist_attraction: 'landmark',
      church: 'place_of_worship',
      shopping_mall: 'shopping',
      hotel: 'lodging',
      gas_station: 'fuel',
      parking: 'parking'
    }

    return categoryMap[category] || category
  }

  /**
   * Verify a POI candidate by querying Mapbox around a target point
   * Returns a confidence score (0-100) and whether a close match exists
   */
  async verifyPlace(
    query: string,
    target: { lat: number; lng: number },
    options?: {
      proximity?: { lat: number; lng: number }
      maxDistanceMeters?: number
      language?: string
    }
  ): Promise<PlaceVerificationResult> {
    if (!MAPBOX_ACCESS_TOKEN) {
      logger.warn('MAPBOX_ACCESS_TOKEN not set, skipping verification')
      return { matched: false, confidence: 0, source: 'mapbox' }
    }

    try {
      const url = new URL(`${this.baseUrl}/${encodeURIComponent(query)}.json`)
      const proximity = options?.proximity ?? target
      url.searchParams.set('proximity', `${proximity.lng},${proximity.lat}`)
      url.searchParams.set('limit', '1')
      url.searchParams.set('types', 'poi')
      url.searchParams.set('access_token', MAPBOX_ACCESS_TOKEN)
      url.searchParams.set('autocomplete', 'false')
      if (options?.language) {
        url.searchParams.set('language', options.language)
      }

      const response = await fetch(url.toString())
      if (!response.ok) {
        throw new Error(`Mapbox verification error: ${response.statusText}`)
      }

      const data = (await response.json()) as {
        features: Array<{
          text?: string
          place_name?: string
          relevance?: number
          geometry: { coordinates: [number, number] }
        }>
      }

      const feature = data.features?.[0]
      if (!feature?.geometry?.coordinates) {
        return { matched: false, confidence: 0, source: 'mapbox' }
      }

      const [lng, lat] = feature.geometry.coordinates
      const distanceMeters = calculateDistance(target, { lat, lng })
      const maxDistanceMeters = options?.maxDistanceMeters ?? 25_000
      const withinRadius = distanceMeters <= maxDistanceMeters
      const relevanceScore = typeof feature.relevance === 'number' ? feature.relevance : 0
      const distanceScore = Math.max(0, 100 - (distanceMeters / maxDistanceMeters) * 100)
      const confidence = Math.round(
        Math.min(100, distanceScore * 0.6 + Math.min(100, relevanceScore * 100) * 0.4)
      )

      return {
        matched: withinRadius,
        confidence,
        distanceMeters,
        name: feature.text || feature.place_name,
        location: { coordinates: { lat, lng } },
        source: 'mapbox'
      }
    } catch (error) {
      logger.error('Mapbox verification error', { error, query })
      return { matched: false, confidence: 0, source: 'mapbox' }
    }
  }
}
